import React, { useCallback, useMemo } from 'react';
import {
  Edit,
  TextField,
  usePermissions,
  useNotify,
  Labeled,
  TabbedShowLayout,
} from 'react-admin';

import {
  Grid,
  Card,
  CardHeader,
  Divider,
  CardContent,
} from '@mui/material';

import hasAccess from '../../utilities/hasAccess';

import actions, { Actions } from '../../constants/actions';

import ActivationButton from '../../customFields/ActivationButton';

import TeamBreadcrumb from './TagBreadcrumb';
import useValidateId from '../../hooks/useValidateId';
import resourceTypes from '../../constants/resourceTypes';
import EditField from '../../customFields/EditField';
import errorNotification from '../../utilities/errorNotification';
import EditToolbar from '../layout/EditToolbar';
import AuditResourceList from '../audit/AuditResourceList';

const TagEdit = () => {
  const { permissions } = usePermissions<Actions[]>();

  const notify = useNotify();

  const onError = useCallback((e: unknown) => {
    notify(errorNotification(e), { type: 'error' });
  }, [notify]);

  const showEdit = useMemo(() => hasAccess(permissions, actions.TAG_UPDATE), [permissions]);

  const validatedId = useValidateId();
  if (!validatedId.isValid) return null;

  return (
    <Edit
      mutationMode="pessimistic"
      redirect={false}
      mutationOptions={{ onError }}
      actions={(
        <EditToolbar>
          {hasAccess(permissions, actions.TAG_UPDATE_ACTIVATION) && (
            <ActivationButton />
          )}
        </EditToolbar>
      )}
    >
      <TeamBreadcrumb />
      <TabbedShowLayout>
        <TabbedShowLayout.Tab label="Information">
          <Grid container spacing={6}>
            <Grid size={12}>
              <Card variant="outlined">
                <CardHeader title="Tag" />
                <Divider />
                <CardContent>
                  <Grid container rowSpacing={2} columnSpacing={4}>
                    <Grid
                      size={{
                        xs: 12,
                        md: 6,
                      }}
                    >
                      <Labeled label="ID">
                        <TextField source="id" />
                      </Labeled>
                    </Grid>
                    <Grid
                      size={{
                        xs: 12,
                        md: 6,
                      }}
                    >
                      <EditField showEdit={showEdit} source="name" label="Name" />
                    </Grid>
                    <Grid
                      size={{
                        xs: 12,
                        md: 6,
                      }}
                    >
                      <EditField showEdit={showEdit} type="color" source="color" label="Color" />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabbedShowLayout.Tab>
        {hasAccess(permissions, actions.AUDIT_TRACK_LIST) && (
          <TabbedShowLayout.Tab label="Audit trail">
            <AuditResourceList resourceType={resourceTypes.TAG} />
          </TabbedShowLayout.Tab>
        )}
      </TabbedShowLayout>
    </Edit>
  );
};

export default TagEdit;
